'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { EGalleryDimension } from '@/types/product'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import 'photoswipe/dist/photoswipe.css'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useProductDetailSafe } from '../context/product-detail-context'
import './photoswipe-custom.css'
import ProductGalleryNavigation from './product-gallery-navigation'
import ProductVideo from './product-video'
import ProductVideoThumbnail from './product-video-thumbnail'

type Props = {
  className?: string
  productTitle: string
}

export const ProductGallery: React.FC<Props> = ({
  className,
  productTitle,
}) => {
  const { colorOptionActive, product, scrollToFirstImageRef } =
    useProductDetailSafe()

  const hasVideo = !!product?.youtube_video
  const productImage =
    colorOptionActive?.image?.slice(0, -1) ||
    product?.images?.slice(0, -1) ||
    []

  const videoRef = useRef<HTMLVideoElement>(null)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)

      if (!hasVideo || !videoRef.current) return

      if (index === 0) {
        videoRef.current.muted = true
        videoRef.current
          .play()
          .then(() => setIsVideoPlaying(true))
          .catch((e) => console.error('Error playing video:', e))
      } else if (isVideoPlaying && !videoRef.current.paused) {
        videoRef.current.pause()
        setIsVideoPlaying(false)
      }
    },
    [emblaApi, hasVideo, isVideoPlaying]
  )

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const newIndex = emblaApi.selectedScrollSnap()
    setSelectedIndex(newIndex)
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())

    if (!hasVideo || !videoRef.current) return

    if (newIndex === 0) {
      videoRef.current.muted = true
      videoRef.current
        .play()
        .then(() => setIsVideoPlaying(true))
        .catch((e) => console.error('Error playing video:', e))
    } else if (!videoRef.current.paused) {
      setIsVideoPlaying(false)
      videoRef.current.pause()
    }
  }, [emblaApi, hasVideo])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    if (!emblaApi || !hasVideo) {
      scrollToFirstImageRef.current = null
      return
    }
    scrollToFirstImageRef.current = () => scrollTo(1)
    return () => {
      scrollToFirstImageRef.current = null
    }
  }, [emblaApi, hasVideo, scrollTo, scrollToFirstImageRef])

  useEffect(() => {
    let lightbox: PhotoSwipeLightbox | null = new PhotoSwipeLightbox({
      gallery: '#' + 'product-gallery',
      children: 'a',
      pswpModule: () => import('photoswipe'),
    })

    if (product?.youtube_video) {
      lightbox.on('uiRegister', () => {
        lightbox?.pswp?.ui?.registerElement({
          name: 'custom-video-support',
          order: 9,
          isButton: false,
          appendTo: 'root',
          onInit: (element: HTMLElement, pswp) => {
            let videoEl: HTMLVideoElement | null = null

            pswp.on('change', () => {
              const currSlide = pswp.currSlide
              if (
                currSlide &&
                currSlide.data.element?.dataset.pswpType === 'video'
              ) {
                const videoSrc = currSlide.data.element?.dataset.pswpVideoSrc
                if (videoSrc) {
                  if (videoEl) {
                    videoEl.pause()
                    videoEl.remove()
                    videoEl = null
                  }

                  videoEl = document.createElement('video')

                  const contentEl =
                    currSlide.container.querySelector('.pswp__content')
                  if (contentEl) {
                    contentEl.innerHTML = ''
                    contentEl.appendChild(videoEl)
                  }
                }
              } else if (videoEl) {
                videoEl.pause()
                videoEl = null
              }
            })

            pswp.on('destroy', () => {
              if (videoEl) {
                videoEl.pause()
                videoEl = null
              }
            })
          },
        })
      })
    }

    lightbox.init()

    return () => {
      if (lightbox) {
        lightbox.destroy()
        lightbox = null
      }
    }
  }, [product?.youtube_video])

  useEffect(() => {
    if (!hasVideo || !videoRef.current) return

    videoRef.current.muted = true

    const handleVideoClick = () => {
      if (!videoRef.current) return

      if (videoRef.current.paused) {
        videoRef.current.muted = true
        videoRef.current
          .play()
          .then(() => setIsVideoPlaying(true))
          .catch((e) => console.error('Error playing video:', e))
      } else {
        setIsVideoPlaying(false)
        videoRef.current.pause()
      }
    }

    const videoElement = videoRef.current
    videoElement.addEventListener('click', handleVideoClick)

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('click', handleVideoClick)
      }
    }
  }, [hasVideo])

  const handleVideoThumbnailClick = useCallback(() => {
    if (!videoRef.current) return

    if (selectedIndex !== 0) {
      scrollTo(0)
      return
    }

    try {
      if (videoRef.current.paused) {
        videoRef.current.muted = true
        videoRef.current
          .play()
          .then(() => {
            setIsVideoPlaying(true)
          })
          .catch((e) => console.error('Error playing video:', e))
      } else {
        setIsVideoPlaying(false)
        videoRef.current.pause()
      }
    } catch (error) {
      console.error('Error toggling video playback:', error)
    }
  }, [selectedIndex, scrollTo])

  // include collections [69b22f304a3f281d3a477e93]
  const isFifaProduct = product?.collections?.some((c) => {
    if (typeof c === 'string') {
      return c === '69b22f304a3f281d3a477e93'
    }
    return c.value === '69b22f304a3f281d3a477e93'
  })

  // include collections [6ab1e99a600ab031c45d18b9]
  const isHelloKittyProduct = product?.collections?.some((c) => {
    if (typeof c === 'string') {
      return c === '6ab1e99a600ab031c45d18b9'
    }
    return c.value === '6ab1e99a600ab031c45d18b9'
  })

  // include collections [6ad03065095911037a562ed2]
  const isHoangSaoProduct = product?.collections?.some((c) => {
    if (typeof c === 'string') {
      return c === '6ad03065095911037a562ed2'
    }
    return c.value === '6ad03065095911037a562ed2'
  })

  const hasIconThumbnail =
    !product?.icon_thumbnail?.hide && !!product?.icon_thumbnail?.image

  return (
    <section
      className={cn(
        'relative lg:sticky lg:top-24 lg:flex lg:h-fit lg:gap-2',
        className
      )}
    >
      <div className='no-scrollbar absolute left-5 top-6 z-[1] flex max-h-[calc(100vh-400px)] flex-col gap-2 overflow-y-auto lg:static lg:gap-1.5'>
        {hasVideo && (
          <ProductVideoThumbnail
            posterSrc={
              productImage[0]?.src
                ? ImageHelper.getImageUrl(productImage[0].src, '270_306')
                : ''
            }
            isPlaying={isVideoPlaying}
            isActive={selectedIndex === 0}
            onClick={handleVideoThumbnailClick}
            productTitle={productTitle}
          />
        )}
        {productImage?.map((item, index) => (
          <button
            key={index}
            className={cn(
              'h-10 min-h-10 w-10 min-w-10 overflow-hidden rounded border border-neutral-300 lg:h-14.5 lg:w-11 lg:border-0',
              selectedIndex !== (hasVideo ? index + 1 : index) && 'opacity-50'
            )}
            onClick={() => scrollTo(hasVideo ? index + 1 : index)}
          >
            <Image
              src={ImageHelper.getImageUrl(item.src, '270_306')}
              alt={productTitle + ' - ' + colorOptionActive?.title}
              width={30}
              height={40}
              className='h-auto w-full object-cover'
              unoptimized
            />
          </button>
        ))}
      </div>

      <div
        className='relative aspect-[538/806] h-full w-full overflow-hidden md:rounded-lg'
        ref={emblaRef}
      >
        <div className='pswp-gallery -ml-2 flex h-full' id={'product-gallery'}>
          {hasVideo && (
            <div
              key={'product-video'}
              className='relative block flex-[0_0_100%] pl-2'
            >
              <ProductVideo
                videoSrc={product?.youtube_video || ''}
                videoRef={videoRef}
                onPlayingChange={setIsVideoPlaying}
              />
            </div>
          )}
          {productImage?.map((item, index) => (
            <a
              href={ImageHelper.getImageUrl(item.src)}
              data-pswp-width={EGalleryDimension.WIDTH}
              data-pswp-height={EGalleryDimension.HEIGHT}
              key={'reviews-gallery' + '-' + index}
              target='_blank'
              rel='noopener noreferrer'
              className='relative block flex-[0_0_100%] cursor-zoom-in pl-2'
            >
              <Image
                src={ImageHelper.getN7ImageUrl(item.src, 1100)}
                alt={productTitle + ' - ' + colorOptionActive?.title}
                width={713}
                height={1050}
                className='h-full w-full object-cover'
                fetchPriority={!hasVideo && index === 0 ? 'high' : 'low'}
                sizes='(max-width: 768px) 100vw, 375px'
                unoptimized={!hasVideo && index === 0}
                priority={!hasVideo && index === 0}
              />
            </a>
          ))}
        </div>
        {isHelloKittyProduct ? (
          <div className='pointer-events-none absolute right-[5%] top-[4.4%] z-[2] w-[20.5%] lg:left-[5%] lg:right-auto'>
            <Image
              src={ImageHelper.getImageUrl(
                '/image/2026/10/16/hello-kitty-collab-logo.png'
              )}
              alt='hello-kitty-logo'
              width={140}
              height={16}
              className='h-auto w-full object-contain'
            />
          </div>
        ) : null}
        {isHoangSaoProduct ? (
          <div className='absolute right-4 top-6 w-[20.5%]'>
            <Image
              src={ImageHelper.getImageUrl(
                '/image/2026/10/20/coolmate-x-hoang-sao-logo.png'
              )}
              alt='coolmate-x-hoang-sao-logo'
              width={600}
              height={169}
              className='h-auto w-full object-contain'
            />
          </div>
        ) : null}
        {(isHelloKittyProduct || hasIconThumbnail) && (
          <div className='pointer-events-none absolute bottom-0 left-0 flex w-full flex-col'>
            {isHelloKittyProduct ? (
              <Image
                src={ImageHelper.getImageUrl(
                  '/image/2026/10/16/hello-kitty-sanrio-legal-stacked.png'
                )}
                alt='sanrio-license-legal'
                width={224}
                height={82}
                className='mb-[3.7%] ml-[3.5%] block h-auto w-[16.4%] object-contain'
              />
            ) : null}
            {hasIconThumbnail && (
              <Image
                src={ImageHelper.getImageUrl(
                  product?.icon_thumbnail?.image as string,
                  '',
                  750
                )}
                alt={productTitle + ' - ' + colorOptionActive?.title}
                width={375}
                height={54}
                className='w-full object-contain'
              />
            )}
          </div>
        )}
      </div>

      {product?.gender_type === 'FEMALE' &&
      !isFifaProduct &&
      !isHelloKittyProduct &&
      !isHoangSaoProduct ? (
        <Image
          src='/images/logo-active-for-women-1.svg'
          width={61}
          height={20}
          className='absolute bottom-1 right-5.5 top-6 w-20'
          alt='Active for women logo'
        />
      ) : null}

      {isFifaProduct ? (
        <div className='absolute right-2 top-2 z-[2] w-10 md:w-14'>
          <Image
            src={ImageHelper.getN7ImageUrl(
              '/image/2026/03/19/logo_fifa_official_1.png'
            )}
            alt='fifa-logo'
            width={80}
            height={120}
            className='h-auto w-full object-contain'
            loading='lazy'
          />
        </div>
      ) : null}

      {(canScrollPrev || canScrollNext) && (
        <ProductGalleryNavigation
          scrollPrev={scrollPrev}
          canScrollPrev={canScrollPrev}
          canScrollNext={canScrollNext}
          scrollNext={scrollNext}
          className={
            !product?.icon_thumbnail?.hide && product?.icon_thumbnail?.image
              ? 'bottom-[4.5rem]'
              : 'bottom-4'
          }
        />
      )}
    </section>
  )
}
